import { ModalProps } from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Button from '@/trio-ui/Button';
import InputText from '@/trio-ui/InputText';
import Modal from '@/trio-ui/Modal';
import { submitTechnicalChallengeModalFormSchema } from '@/utils/formsValidation';
import * as Styles from './SubmitTechnicalChallengeModal.styles';
import TextEditor from '../TextEditor';
import UploadFileRow from '../UploadFileRow';

export type FormData = yup.Asserts<typeof submitTechnicalChallengeModalFormSchema>;

export type Props = Pick<ModalProps, 'onClose' | 'isOpen'> & {
  onSubmit: (data: {
    githubLink: string;
    title: string;
    technicalDesign: string;
    videoPresentation: File;
  }) => void;
  isLoading?: boolean;
};

export default function SubmitTechnicalChallengeModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}: Props): JSX.Element {
  const { handleSubmit, values, handleChange, errors, setFieldValue } = useFormik<
    yup.TypeOf<typeof submitTechnicalChallengeModalFormSchema>
  >({
    initialValues: {
      githubLink: '',
      title: '',
      technicalDesign: '',
      videoPresentation: null,
    },
    validationSchema: submitTechnicalChallengeModalFormSchema,
    onSubmit: (data) => {
      const { githubLink, title, technicalDesign, videoPresentation } = data as FormData;

      if (videoPresentation) {
        onSubmit?.({
          githubLink,
          title,
          technicalDesign,
          videoPresentation,
        });
      }
    },
  });

  const isFormValid =
    values.githubLink && values.technicalDesign && values.title && values.videoPresentation;

  const Body = (
    <Styles.Content>
      <form onSubmit={handleSubmit}>
        <InputText
          label="Github Link"
          isLabelAboveField
          isLink
          value={values.githubLink || ''}
          onChangeHandler={handleChange}
          name="githubLink"
          errorMessage={errors.githubLink}
        />
        <Styles.Divider />
        <Styles.SectionTitle>Technical Design</Styles.SectionTitle>
        <Styles.Fields>
          <InputText
            name="title"
            label="Title"
            isLabelAboveField
            value={values.title || ''}
            onChangeHandler={handleChange}
            errorMessage={errors.title}
          />
          <TextEditor
            value={values.technicalDesign || ''}
            onChangeText={(text) => setFieldValue('technicalDesign', text)}
            isEditing
            hideButtons
            hideEditButton
            errorMessage={errors.technicalDesign}
          />
        </Styles.Fields>
        <Styles.Divider />
        <Styles.SectionTitle>Video Presentation</Styles.SectionTitle>
        <UploadFileRow
          file={values.videoPresentation || null}
          onAddFile={(event) => setFieldValue('videoPresentation', event.target.files?.[0] || null)}
          onRemoveFile={() => setFieldValue('videoPresentation', null)}
          acceptedFileTypes="video/*"
        />
        <Styles.Footer>
          <Button type="submit" disabled={isLoading || !isFormValid}>
            Submit
          </Button>
          <Button type="button" onClick={onClose} variantStyle="outline" disabled={isLoading}>
            Cancel
          </Button>
        </Styles.Footer>
      </form>
    </Styles.Content>
  );

  return (
    <Modal
      modalId="technical-design-modal"
      isCentered
      size="4xl"
      modalTitle="Submit the interview"
      bodyRender={Body}
      isOpen={isOpen}
      onClose={onClose}
      hideCancelButton
    />
  );
}
